/**
 * User Configuration – migrated from admin4.0 GlobalUserAccess.js.
 * Same layout, design, flow: DataGrid, Add/Edit modal, profile picture with webcam & file upload.
 */
import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  IconButton,
  Autocomplete,
  InputAdornment,
  FormControlLabel,
  Switch,
  Tooltip,
  Typography,
  Stack,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import PhotoCamera from "@mui/icons-material/PhotoCamera";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CancelIcon from "@mui/icons-material/Cancel";
import Webcam from "react-webcam";
import { Colors } from "../../../Styles/Colors";
import RestAPIService from "../../../Utility/Services/RestAPIService";
import { Serviceurls } from "../../../Utility/API/Serviceurls";
import ToastMsg from "../../../Components/ToastMsg/ToastMsg";
import {
  CommonEditButton,
  CommonGridToolBarWithFilterText,
} from "../../../Components/Common Components/CommonComponents";
import { ImagePaths } from "../../../Utility/ImagePaths";
import Loader from "../../../Components/Loader";
import CommonValidation from "../../../Utility/CommonValidation";
import "../../../Styles/Configuration.css";
import "../../../Components/Common Components/OpenWebCamPopUP.css";

/** Simple password validation: returns error message string or false if valid. */
function passwordValidations(password) {
  if (!password || !password.trim()) return false;
  if (password.length < 6) return "Password should be at least 6 characters";
  if (password.length > 50) return "Password should be at most 50 characters";
  return false;
}

const initialFormData = {
  first_name_key: "",
  last_name_key: "",
  mobile_number: "",
  email: "",
  role_id: "",
  registration_no: "",
  notes: "",
  username_key: "",
  password_key: "",
  UserIsInActive: false,
};

export default function UserConfig() {
  const [state, setState] = useState({
    user_access_data: [],
    is_loader: false,
    selected_ids: [],
    page: 0,
    pageSize: 10,
    isErrorMsg: false,
    isErrorMsgText: "",
    successMsg: false,
    successMsgText: "",
  });

  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("add");
  const [editId, setEditId] = useState(null);
  const [tempProfilePic, setTempProfilePic] = useState(null);
  const [profilePic, setProfilePic] = useState(null);
  const [picUploadopen, setPicUploadopen] = useState(false);
  const [webCamOpen, setWebCamOpen] = useState(false);
  const [roleList, setRoleList] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const webPicRef = React.useRef(null);

  const updateState = (updates) => setState((prev) => ({ ...prev, ...updates }));
  const successMessage = (message) => updateState({ successMsg: true, successMsgText: message });
  const errorMessage = (message) => updateState({ isErrorMsg: true, isErrorMsgText: message });
  const msgClose = () =>
    updateState({ isErrorMsg: false, isErrorMsgText: "", successMsg: false, successMsgText: "" });

  useEffect(() => {
    getUserAccessList();
    getRoles();
  }, []);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const validateForm = () => {
    const { first_name_key, email, mobile_number, role_id, password_key, username_key } = formData;
    const passWord = passwordValidations(password_key);
    const validations = [
      { valid: first_name_key?.trim(), msg: "First Name is required" },
      { valid: email?.trim(), msg: "Email is required" },
      { valid: /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email), msg: "Invalid Email format", when: !!email?.trim() },
      { valid: mobile_number?.trim(), msg: "Mobile Number is required" },
      { valid: /^[0-9]{10}$/.test(mobile_number), msg: "Mobile Number must be 10 digits", when: !!mobile_number?.trim() },
      { valid: role_id, msg: "Role is required" },
      { valid: username_key?.trim(), msg: "User Name is required" },
    ];
    for (const { valid, msg, when = true } of validations) {
      if (!valid && when) {
        errorMessage(msg);
        return false;
      }
    }
    if (mode === "add") {
      if (!password_key?.trim()) {
        errorMessage("Password is required for new users");
        return false;
      }
    }
    if (password_key && passWord) {
      errorMessage(passWord);
      return false;
    }
    return true;
  };

  /** GET /histopathology/user-access/role-mapping/ – list login users with role_master_id, role_name (branch from JWT). */
  const getUserAccessList = useCallback(() => {
    updateState({ is_loader: true });
    RestAPIService.getAll(Serviceurls.HISTOPATHOLOGY_USER_ACCESS_ROLE_MAPPING)
      .then((response) => {
        if (response?.data?.status === "success") {
          const Data = response.data.data || [];
          updateState({
            user_access_data: Array.isArray(Data) ? Data.map((el, index) => ({ ...el, sno: index + 1 })) : [],
            is_loader: false,
          });
        } else {
          updateState({ is_loader: false });
        }
      })
      .catch((e) => {
        updateState({ is_loader: false });
        errorMessage(e?.response?.data?.message || e?.message);
      });
  }, []);

  const getRoles = useCallback(() => {
    RestAPIService.getAll(Serviceurls.HISTOPATHOLOGY_ROLE_MASTER)
      .then((response) => {
        if (response?.data?.status === "success") {
          const data = response.data.data || [];
          setRoleList(Array.isArray(data) ? data : []);
        }
      })
      .catch(() => {});
  }, []);

  const dataURItoBlob = async (dataURI) => {
    if (!dataURI?.startsWith("data:")) {
      const response = await fetch(dataURI);
      return await response?.blob();
    }
    const byteString = atob(dataURI.split(",")[1]);
    const mimeString = dataURI.split(",")[0].split(":")[1].split(";")[0];
    const ab = new ArrayBuffer(byteString?.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString?.length; i++) ia[i] = byteString?.charCodeAt(i);
    return new Blob([ab], { type: mimeString });
  };

  const handleOpenAdd = () => {
    setMode("add");
    setEditId(null);
    setFormData({ ...initialFormData });
    setProfilePic(null);
    setPicUploadopen(false);
    setOpen(true);
  };

  const handleOpenEdit = (row) => {
    setMode("edit");
    setEditId(row.id);
    const roleObj = roleList?.find((item) => item?.id === row?.role_master_id) || null;
    setFormData({
      first_name_key: row.first_name || "",
      last_name_key: row.last_name || "",
      mobile_number: row.mobile_number || "",
      email: row.email || "",
      role_id: roleObj,
      registration_no: row.register_no || "",
      notes: row.notes || "",
      username_key: row.username || "",
      password_key: "",
      UserIsInActive: !!row.is_deactivated,
    });
    setProfilePic(row.user_image || null);
    setPicUploadopen(false);
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleSubmit = async () => {
    if (!validateForm()) return;
    updateState({ is_loader: true });
    const roleMasterId = formData.role_id?.id != null ? formData.role_id.id : null;
    const payload = {
      first_name: (formData.first_name_key || "").trim(),
      last_name: (formData.last_name_key || "").trim(),
      mobile_number: (formData.mobile_number || "").trim(),
      email: (formData.email || "").trim(),
      username: (formData.username_key || "").trim(),
      password: (formData.password_key || "").trim(),
      role_master_id: roleMasterId,
      register_no: (formData.registration_no || "").trim() || undefined,
      notes: (formData.notes || "").trim() || undefined,
      is_deactivated: !!formData.UserIsInActive,
    };

    RestAPIService.create(payload, Serviceurls.HISTOPATHOLOGY_USER_ACCESS_ROLE_MAPPING)
      .then((response) => {
        if (response?.data?.status === "success") {
          getUserAccessList();
          successMessage(response?.data?.message || "Saved successfully");
          handleClose();
        }
        updateState({ is_loader: false });
      })
      .catch((e) => {
        errorMessage(e?.response?.data?.message || e?.message);
        updateState({ is_loader: false });
      });
  };

  const handleUpdate = async () => {
    if (!validateForm()) return;
    updateState({ is_loader: true });
    const roleMasterId = formData.role_id?.id != null ? formData.role_id.id : null;
    const payload = {
      id: editId,
      first_name: (formData.first_name_key || "").trim(),
      last_name: (formData.last_name_key || "").trim(),
      mobile_number: (formData.mobile_number || "").trim(),
      email: (formData.email || "").trim(),
      username: (formData.username_key || "").trim(),
      role_master_id: roleMasterId,
      register_no: (formData.registration_no || "").trim() || undefined,
      notes: (formData.notes || "").trim() || undefined,
      is_deactivated: !!formData.UserIsInActive,
    };
    if (formData.password_key?.trim()) payload.password = formData.password_key.trim();

    RestAPIService.create(payload, Serviceurls.HISTOPATHOLOGY_USER_ACCESS_ROLE_MAPPING)
      .then((response) => {
        if (response?.data?.status === "success") {
          getUserAccessList();
          successMessage(response?.data?.message || "Updated successfully");
          handleClose();
        }
        updateState({ is_loader: false });
      })
      .catch((e) => {
        errorMessage(e?.response?.data?.message || e?.message);
        updateState({ is_loader: false });
      });
  };

  const OpenWebCamPopUP = () => (
    <Box>
      <Dialog open fullWidth maxWidth="sm" PaperProps={{ sx: { position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)", m: 0 } }}>
        <Box className="eMed_WebCamPop_Main" sx={{ p: 0 }}>
          <Box className="eMed_WebCam_Header">
            <Typography variant="h6">Profile Picture</Typography>
            <IconButton
              onClick={() => {
                setTempProfilePic(profilePic);
                setWebCamOpen(false);
              }}
            >
              <img src={ImagePaths.LabCloseButton?.default ?? ImagePaths.LabCloseButton} alt="close" className="eMed_action_img_small" />
            </IconButton>
          </Box>
          <Box component="div">
            {tempProfilePic === null ? (
              <Webcam
                className="eMed_WebCam_Div"
                audio={false}
                mirrored
                ref={webPicRef}
                screenshotFormat="image/jpeg"
              />
            ) : (
              <img className="eMed_WebCam_Img_Div" src={tempProfilePic} alt="Capture" />
            )}
          </Box>
          <Box className="eMed_WebCam_Btn_div" width="70%">
            <Button
              disabled={tempProfilePic !== null}
              variant="contained"
              size="small"
              onClick={() => setTempProfilePic(webPicRef.current?.getScreenshot?.() ?? null)}
            >
              Capture
            </Button>
            <Button variant="contained" size="small" onClick={() => setTempProfilePic(null)}>
              Retake
            </Button>
            <Button
              disabled={tempProfilePic === null}
              variant="contained"
              size="small"
              onClick={() => {
                setProfilePic(tempProfilePic);
                setWebCamOpen(false);
              }}
            >
              Set Profile
            </Button>
          </Box>
        </Box>
      </Dialog>
    </Box>
  );

  const TruncateTooltip = ({ text, max = 15, fontSize = "0.9dvw" }) => {
    if (!text) return <Box fontSize={fontSize}>-</Box>;
    const display = text.length > max ? text.slice(0, max) + "..." : text;
    return text.length > max ? (
      <Tooltip placement="top" title={text}>
        <Box fontSize={fontSize} noWrap>
          {display}
        </Box>
      </Tooltip>
    ) : (
      <Box fontSize={fontSize} noWrap>
        {text}
      </Box>
    );
  };

  const getBase64 = (file, cb) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => cb(reader.result);
    reader.onerror = () => {};
  };

  const UploadImage = (e) => {
    const file = e.target.files?.[0];
    if (file) getBase64(file, (result) => { setProfilePic(result); setPicUploadopen(false); });
  };

  const columns = [
    // {
    //   field: "profile_pic",
    //   headerName: "Profile",
    //   flex: 0.2,
    //   renderCell: ({ row }) => (
    //     <div className="eMed_Com_Patient_Avatar">
    //       <img
    //         className="eMed_Com_Patient_AvaIcon"
    //         src={row?.user_image ? row.user_image : ImagePaths.Male?.default ?? ImagePaths.Male ?? ImagePaths.Default}
    //         alt="Avatar"
    //       />
    //     </div>
    //   ),
    // },
    {
      field: "first_name",
      headerName: "User Information",
      flex: 0.4,
      renderCell: ({ row }) => (
        <Box>
          <TruncateTooltip text={row?.first_name} max={30} />
          <TruncateTooltip text={row?.mobile_number} max={15} fontSize="0.8dvw" />
        </Box>
      ),
      valueGetter: (params) => `${params.row?.first_name || ""} ${params.row?.mobile_number || ""}`,
    },
    { field: "username", headerName: "Username", flex: 0.3, renderCell: ({ row }) => <TruncateTooltip text={row?.username} max={22} /> },
    { field: "register_no", headerName: "Reg No", flex: 0.2, renderCell: ({ row }) => <TruncateTooltip text={row?.register_no} max={18} /> },
    { field: "role_name", headerName: "Role", flex: 0.2, renderCell: ({ row }) => <TruncateTooltip text={row?.role_name} max={18} /> },
    {
      field: "status",
      headerName: "Status",
      flex: 0.2,
      renderCell: ({ row }) => (
        <Box fontSize="0.9dvw" color={!row?.is_deactivated ? "green" : "red"}>
          {!row?.is_deactivated ? "Active" : "Inactive"}
        </Box>
      ),
      valueGetter: (params) => (params?.row?.is_deactivated ? "Inactive" : "Active"),
    },
    {
      field: "action",
      headerName: "Action",
      flex: 0.2,
      renderCell: ({ row }) => (
        <Stack direction="row" alignItems="center" spacing={2} sx={{ marginTop: "0.3vw" }}>
          <CommonEditButton size="1.5vw" disable={state?.selected_ids?.length > 0} onClick={() => handleOpenEdit(row)} />
        </Stack>
      ),
    },
  ];

  const gridToolBar = () => (
    <Box>
      <CommonGridToolBarWithFilterText fromStore searchHolder="Search user name, role..." />
    </Box>
  );

  return (
    <Box sx={{ height: "79.5vh", width: "94vw", backgroundColor: Colors.Background, padding: "0.5vw" }}>
      <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", mb: "0.5vw" }}>
        <Button size="small" variant="contained" sx={{ textTransform: "capitalize" }} onClick={handleOpenAdd}>
          Add User
        </Button>
      </Box>
      <Box sx={{ height: "70vh", width: "93vw", marginTop: "0.5vw", backgroundColor: "white" }}>
        <DataGrid
          rows={state.user_access_data}
          columns={columns}
          getRowId={(row) => row.id ?? row.sno}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 20, 30]}
          pagination
          headerHeight={40}
          rowHeight={40}
          density="standard"
          disableRowSelectionOnClick
          loading={state.is_loader}
          showToolbar
          localeText={{ toolbarColumns: "", toolbarDensity: "", toolbarExport: "", toolbarFilters: "", toolbarExportPrint: "" }}
          slots={{
            toolbar: gridToolBar,
            noRowsOverlay: () => (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                No Records To Be Shown
              </Box>
            ),
          }}
        />
      </Box>

      {state.isErrorMsg && <ToastMsg severity="error" msg={state.isErrorMsgText} msgPop={msgClose} />}
      {state.successMsg && <ToastMsg severity="success" msg={state.successMsgText} msgPop={msgClose} />}

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <Box sx={{ display: "flex", paddingRight: 2 }}>
          <DialogTitle>User Details</DialogTitle>
          <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 2, flexGrow: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  color="error"
                  size="small"
                  checked={formData?.UserIsInActive}
                  onChange={(e) => setFormData((prev) => ({ ...prev, UserIsInActive: e.target.checked }))}
                />
              }
              label="In-Active User"
            />
            <IconButton onClick={handleClose}>X</IconButton>
          </Box>
        </Box>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sx={{ textAlign: "center" }}>
              <Box sx={{ display: "flex", justifyContent: "center", marginTop: "1vw", alignItems: "center" }}>
                <img
                  src={profilePic || ImagePaths.Male?.default || ImagePaths.Male || ImagePaths.Default}
                  onClick={() => setPicUploadopen(true)}
                  alt="profile"
                  className={profilePic ? "eMed_new_img_res_ui" : "eMed_new_profile_ui"}
                />
              </Box>
              <Box sx={{ display: "flex", justifyContent: "center", visibility: picUploadopen ? "visible" : "hidden" }}>
                <IconButton color="primary" onClick={() => { setTempProfilePic(null); setWebCamOpen(true); }}>
                  <PhotoCamera />
                </IconButton>
                <IconButton color="primary" component="label">
                  <input hidden accept="image/*" type="file" onChange={UploadImage} />
                  <UploadFileIcon />
                </IconButton>
                <IconButton color="primary" onClick={() => setPicUploadopen(false)}>
                  <CancelIcon />
                </IconButton>
              </Box>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Upload Profile Picture
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="First Name *"
                name="first_name_key"
                size="small"
                fullWidth
                inputProps={{ maxLength: 50 }}
                value={formData.first_name_key}
                onChange={(e) => {
                  if (CommonValidation.alphabetOnly(e.target.value) || e.target.value === "") handleChange(e);
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Last Name"
                name="last_name_key"
                size="small"
                fullWidth
                inputProps={{ maxLength: 50 }}
                value={formData.last_name_key}
                onChange={(e) => {
                  if (CommonValidation.alphabetOnly(e.target.value) || e.target.value === "") handleChange(e);
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Mobile Number *"
                name="mobile_number"
                size="small"
                inputProps={{ maxLength: 10 }}
                fullWidth
                value={formData.mobile_number}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^\d{0,10}$/.test(value)) setFormData((prev) => ({ ...prev, mobile_number: value }));
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Mail ID *"
                name="email"
                size="small"
                inputProps={{ maxLength: 50 }}
                fullWidth
                value={formData.email}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={6}>
              <Autocomplete
                size="small"
                options={roleList}
                value={formData?.role_id ?? null}
                getOptionLabel={(opt) => (typeof opt === "string" ? opt : opt?.role_name ?? "")}
                onChange={(event, newValue) => setFormData((prev) => ({ ...prev, role_id: newValue }))}
                renderInput={(params) => <TextField {...params} label="Select Role *" />}
                slotProps={{ paper: { sx: { maxWidth: "30vw", whiteSpace: "normal", wordBreak: "break-word" } } }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Reg No"
                name="registration_no"
                size="small"
                inputProps={{ maxLength: 50 }}
                fullWidth
                value={formData.registration_no}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Notes"
                name="notes"
                size="small"
                inputProps={{ maxLength: 50 }}
                fullWidth
                maxRows={2}
                multiline
                value={formData.notes}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Username *"
                name="username_key"
                size="small"
                inputProps={{ maxLength: 50 }}
                autoComplete="new-password"
                fullWidth
                value={formData.username_key}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label={`Password ${mode === "add" ? "*" : ""}`}
                name="password_key"
                size="small"
                autoComplete="new-password"
                type={showPassword ? "text" : "password"}
                fullWidth
                inputProps={{ maxLength: 50 }}
                value={formData.password_key}
                onChange={(e) => {
                  if (CommonValidation.PasswordInput(e.target.value) || e.target.value === "") handleChange(e);
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)}>
                        {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button size="small" disabled={state.is_loader} sx={{ textTransform: "capitalize" }} onClick={handleClose} variant="outlined">
            Cancel
          </Button>
          <Button
            size="small"
            sx={{ textTransform: "capitalize" }}
            onClick={mode === "add" ? handleSubmit : handleUpdate}
            color="primary"
            disabled={state.is_loader}
            variant="contained"
          >
            {mode === "add" ? "Add" : "Update"}
          </Button>
        </DialogActions>
      </Dialog>

      <Loader loaderOpen={state.is_loader} />
      {webCamOpen ? OpenWebCamPopUP() : null}
    </Box>
  );
}
